import { BoundMultiSelectField, BoundSelectField, BoundTreeSelectField, useComputed } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { useEffect, useState } from "react";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import { BidScopeSource, ProjectTypeFilter } from "src/generated/graphqlTypes";
import { AddBidPackagePage_OriginatingProjectFragment } from "src/routes/bids/add-bid-package/AddBidPackagePage.generated";
import { BidPackageDetailsStep_BidScopeSourceFragment } from "src/routes/bids/add-bid-package/components/1.BidPackageDetailsStep.generated";
import { useBidScopeFieldsQuery } from "src/routes/bids/add-bid-package/components/BidScopeFields.generated";
import { EditBidPackageFormState } from "../../components/EditBidPackageModal";
import { BidPackageFormState } from "../AddBidPackagePage";

type BidScopeFieldsProps = {
  formState: ObjectState<BidPackageFormState> | ObjectState<EditBidPackageFormState>;
  markets: NamedFragment[];
  scopeSources: BidPackageDetailsStep_BidScopeSourceFragment[];
  disabled?: boolean | { market: boolean; scopeSource: boolean; projects: boolean };
  originatingProject?: AddBidPackagePage_OriginatingProjectFragment;
};

export function BidScopeFields({
  formState,
  markets,
  scopeSources,
  disabled = false,
  originatingProject,
}: BidScopeFieldsProps) {
  const [prefillProjects, setPrefillProjects] = useState(false);
  const marketId = useComputed(() => formState.marketId.value, [formState.marketId.value]);
  /*
    This query always pull back communities, it's children, and their children.
    This is because when using the tree selects below to choose a subdivision, we still want to group by communities.
    Even though we are only allowing users to select subdivisions or the leaves of the tree.
    Pulling back lots or the children of subdivisions is used purely to show the lot count in the subdivision tree select.
    TODO: this could be optimized if projects exposed a `childCount` field
  */
  const { data } = useBidScopeFieldsQuery({
    variables: { projectFilter: { market: [marketId!], type: [ProjectTypeFilter.Community] } },
    skip: !marketId,
  });

  const projects = (data?.projects ?? []).map((p) => ({
    ...p,
    childCount: p.children.length,
    children: p.children.map((c) => ({ ...c, children: [] as never[], childCount: c.children.length })),
  }));

  useEffect(() => {
    if (prefillProjects && projects.nonEmpty && originatingProject) {
      const scopeSource = formState.scopeSource.value;
      switch (scopeSource) {
        // If we are selecting from communities we want to prefill only the project the user came from
        case BidScopeSource.Community:
        case BidScopeSource.ProductOffering:
          const p = projects.find((p) => p.id === originatingProject.id);
          formState.projects.set(p ? [p.id] : []);
          break;
        /*
          If we are selecting from subdivisions we want to prefill the project the user came from and all of its children
          Meaning if a user comes from a community we will prefill all the subdivisions in that community
        */
        case BidScopeSource.Subdivision:
        case BidScopeSource.Lot:
          const availableProjectIds = projects.flatMap((p) => p.children).map((c) => c.id);
          const prefillIds = [originatingProject.id, ...originatingProject.children.map((c) => c.id)];
          formState.projects.set(availableProjectIds.filter((id) => prefillIds.includes(id)));
          break;
      }
      setPrefillProjects(false);
    }
  }, [prefillProjects, projects, originatingProject, formState]);

  const projectsWithoutChildren = projects.filter((p) => p.childCount === 0).map((p) => p.id);

  // If we are creating a bid package from a subdivision, we want to filter down the available scope sources
  if (originatingProject?.__typename === "Subdivision") {
    scopeSources = scopeSources.filter((s) => [BidScopeSource.Subdivision, BidScopeSource.Lot].includes(s.code));
  }

  return (
    <Observer>
      {() => (
        <>
          {!originatingProject && (
            <BoundSelectField
              required
              options={markets}
              field={formState.marketId}
              onSelect={(option) => {
                // Clear out projects when the market changes
                formState.projects.set([]);
                formState.marketId.set(option);
              }}
              disabled={disabled instanceof Object ? disabled.market : disabled}
            />
          )}
          <BoundSelectField
            required
            options={scopeSources}
            field={formState.scopeSource}
            getOptionValue={(option) => option.code}
            getOptionLabel={(option) => option.name}
            onSelect={(option) => {
              // Clear out projects when scope source changes
              formState.projects.set([]);
              formState.scopeSource.set(option);
              // If we are coming from a project and we are creating a bid package, we should try to pre-fill the projects
              if (originatingProject && !(formState as ObjectState<BidPackageFormState>).bidPackageGroupId.value) {
                setPrefillProjects(true);
              }
            }}
            disabled={disabled instanceof Object ? disabled.scopeSource : disabled}
          />
          {formState.marketId.value && formState.scopeSource.value && (
            <>
              {formState.scopeSource.value === BidScopeSource.Community && (
                <BoundMultiSelectField
                  required
                  label="Communities"
                  options={projects}
                  field={formState.projects}
                  disabled={disabled instanceof Object ? disabled.projects : disabled}
                />
              )}
              {formState.scopeSource.value === BidScopeSource.Subdivision && (
                <BoundTreeSelectField
                  required
                  label="Subdivisions"
                  options={projects}
                  field={formState.projects}
                  onSelect={(options) => formState.projects.set(options.leaf.values)}
                  disabledOptions={projectsWithoutChildren}
                  disabled={disabled instanceof Object ? disabled.projects : disabled}
                />
              )}
              {formState.scopeSource.value === BidScopeSource.Lot && (
                <BoundTreeSelectField
                  required
                  label="Lots"
                  options={projects}
                  getOptionLabel={(option) =>
                    option.childCount && option.__typename === "Subdivision"
                      ? `${option.name} - ${option.childCount} Lots`
                      : option.name
                  }
                  field={formState.projects}
                  onSelect={(options) => formState.projects.set(options.leaf.values)}
                  disabledOptions={projectsWithoutChildren}
                  disabled={disabled instanceof Object ? disabled.projects : disabled}
                />
              )}
              {formState.scopeSource.value === BidScopeSource.ProductOffering && (
                <BoundMultiSelectField
                  label="Communities"
                  options={projects}
                  field={formState.projects}
                  nothingSelectedText="All"
                  disabled={disabled instanceof Object ? disabled.projects : disabled}
                />
              )}
            </>
          )}
        </>
      )}
    </Observer>
  );
}
